import type { PropsWithChildren } from "react";
import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { ErrorState } from "@/app/components/legacy-components";
import { usePatientQuery } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { billingQueriesStore } from "@/features/billing/api";
import { LogoLoader } from "@/shared/components";
import { Subscription } from "@/shared/domains";
import type { Patient } from "@/shared/models/legacy-types";

interface IntakeFormContextProps {
  patient: Patient;
  subscription: Subscription;
  completed: boolean;

  actions: {
    setCompleted: (completed: boolean) => void;
  };
}

const IntakeFormContext = createContext<IntakeFormContextProps | null>(null);

export const useIntakeFormContext = () => {
  const context = useContext(IntakeFormContext);

  if (!context) {
    throw new Error(
      "useIntakeFormContext must be used within a <IntakeFormProvider />",
    );
  }

  return context;
};

export const IntakeFormContextProviderWrapper = ({
  children,
}: PropsWithChildren) => {
  const { productId } = useParams();

  const {
    data: subscriptionFromProductId,
    isLoading: isLoadingSubscription,
    isError: isErrorSubscription,
    isSuccess: isSuccessSubscription,
  } = useQuery({
    ...billingQueriesStore.subscriptions._ctx.active,
    select: (subscriptions) =>
      subscriptions.find(
        (subscription) => subscription.currentProduct.id === Number(productId),
      ),
  });
  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    isSuccess: isSuccessPatient,
  } = usePatientQuery();

  const isLoading = isLoadingSubscription || isLoadingPatient;
  const isError = isErrorSubscription || isErrorPatient;
  const isSuccess = isSuccessSubscription && isSuccessPatient;

  if (isError) {
    return <ErrorState className="-translate-y-10 border-none" />;
  }

  if (isLoading) {
    return <LogoLoader withOverlay />;
  }

  if (isSuccess) {
    return (
      <IntakeFormContextProvider
        subscriptionFromProductId={subscriptionFromProductId}
        patient={patient}
      >
        {children}
      </IntakeFormContextProvider>
    );
  }

  return null;
};

const IntakeFormContextProvider = ({
  subscriptionFromProductId,
  patient,
  children,
}: PropsWithChildren<{
  subscriptionFromProductId?: Subscription;
  patient: Patient;
}>) => {
  const location = useLocation();
  const parsedState = Subscription.safeParse(location.state);

  const [subscription] = useState(
    parsedState.success ? parsedState.data : subscriptionFromProductId,
  );
  const [completed, setCompleted] = useState(false);

  if (!subscription) {
    return <Navigate to={ROUTES.BASE} />;
  }

  return (
    <IntakeFormContext.Provider
      value={{
        patient,
        subscription,
        completed,

        actions: { setCompleted },
      }}
    >
      {children}

      <Outlet />
    </IntakeFormContext.Provider>
  );
};
